import { combineEpics } from 'redux-observable';
import { filter, map, mapTo, merge, mergeMap, of } from 'rxjs';
import { isOfType } from 'typesafe-actions';
import { catchAxiosError, catchUnauthorizedError, createEpic$ } from 'lib/store';
import { notificationError, notificationInfo } from 'store/notifications';
import { systemUnauthorized } from 'store/system';
import { bankTransactionSelect } from 'store/transactions';
import {
  bankTransactionArchiveResponse,
  bankTransactionArchiveResult,
  BankTransactionHandleAction,
  bankTransactionHandleError,
  bankTransactionRestoreResponse,
} from './actions';

const bankTransactionsArchiveEpic$ = createEpic$(({ action$, archiveTransaction$ }) =>
  action$.pipe(
    filter(isOfType(BankTransactionHandleAction.ArchiveRequest)),
    mergeMap(action =>
      archiveTransaction$({
        bankTransactions: action.payload,
      }).pipe(
        mergeMap(response =>
          merge(
            of(bankTransactionArchiveResponse(response)),
            of(bankTransactionArchiveResult({ ...response, bankTransactions: action.payload })),
          ),
        ),
        catchAxiosError(bankTransactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const bankTransactionsArchiveSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(BankTransactionHandleAction.ArchiveResponse)),
    mapTo(
      notificationInfo({
        caption: 'Success',
        message: `Bank transactions successfully archived`,
      }),
    ),
  ),
);

const bankTransactionsClearSelectionEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(
      isOfType([
        BankTransactionHandleAction.ArchiveResponse,
        BankTransactionHandleAction.RestoreResponse,
      ]),
    ),
    mapTo(bankTransactionSelect([])),
  ),
);

const bankTransactionHandleErrorEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(BankTransactionHandleAction.Error)),
    mapTo(
      notificationError({
        caption: 'Error',
        duration: 10000,
        message: 'There was a problem processing bank transactions',
      }),
    ),
  ),
);

const bankTransactionsRestoreEpic$ = createEpic$(({ action$, restoreTransaction$ }) =>
  action$.pipe(
    filter(isOfType(BankTransactionHandleAction.RestoreRequest)),
    mergeMap(action =>
      restoreTransaction$({
        mappedTransactionMappingIds: action.payload,
      }).pipe(
        map(() => bankTransactionRestoreResponse(action.payload)),
        catchAxiosError(bankTransactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const bankTransactionsRestoreSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(BankTransactionHandleAction.RestoreResponse)),
    mapTo(
      notificationInfo({
        caption: 'Success',
        message: `Bank transactions successfully restored`,
      }),
    ),
  ),
);

export default combineEpics(
  bankTransactionsArchiveEpic$,
  bankTransactionsArchiveSuccessEpic$,
  bankTransactionsClearSelectionEpic$,
  bankTransactionHandleErrorEpic$,
  bankTransactionsRestoreEpic$,
  bankTransactionsRestoreSuccessEpic$,
);
